import { useEffect, useState } from "react";

/**
 * 1) Tries your FastAPI proxy: /api/wordpress/* (no browser CORS to WordPress).
 *    In prod this only works if your host routes /api → FastAPI (not static index.html).
 * 2) Falls back to direct WordPress REST. Apex nvseeds.com is normalized to www (matches WP canonical URLs).
 *
 * REACT_APP_WORDPRESS_REST_BASE — optional full REST base if WordPress is on another host.
 */

/** WordPress uses www in permalinks; apex + www mixed requests caused CORS issues — always use www for this host. */
function preferWwwNvseedsOrigin(origin) {
  if (!origin) return origin;
  try {
    const u = new URL(origin);
    if (u.hostname === "nvseeds.com") u.hostname = "www.nvseeds.com";
    return u.origin;
  } catch {
    return origin;
  }
}

function preferWwwNvseedsRestBase(fullUrl) {
  if (!fullUrl) return fullUrl;
  try {
    const u = new URL(fullUrl);
    if (u.hostname === "nvseeds.com") u.hostname = "www.nvseeds.com";
    return u.href.replace(/\/$/, "");
  } catch {
    return fullUrl.replace(/\/$/, "");
  }
}

function getApiBase() {
  if (process.env.REACT_APP_BACKEND_URL) {
    return `${process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "")}/api`;
  }
  if (process.env.NODE_ENV === "development") {
    return "/api";
  }
  const site = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  if (site) return `${site}/api`;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  return "/api";
}

function getDirectWordPressRestBase() {
  const full = process.env.REACT_APP_WORDPRESS_REST_BASE;
  if (full) return preferWwwNvseedsRestBase(full);
  const site = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  if (site) return `${preferWwwNvseedsOrigin(site)}/blog/wp-json/wp/v2`;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${preferWwwNvseedsOrigin(window.location.origin)}/blog/wp-json/wp/v2`;
  }
  return "https://www.nvseeds.com/blog/wp-json/wp/v2";
}

/** WordPress single-post URL. REST returns `link` (canonical); the SPA has no /blog/:slug route. */
function wordPressPostPermalink(post) {
  if (post?.link) return post.link;
  const rest = getDirectWordPressRestBase();
  const blogBase = rest.replace(/\/wp-json\/wp\/v2\/?$/i, "");
  return `${blogBase.replace(/\/$/, "")}/${post.slug}/`;
}

async function parseJsonResponse(res) {
  const text = await res.text();
  const t = text.trim();
  if (!t || t.startsWith("<")) return null;
  try {
    return JSON.parse(t);
  } catch {
    return null;
  }
}

async function fetchCategories(slug, apiBase, wpBase) {
  const q = `slug=${encodeURIComponent(slug)}&hide_empty=false`;
  let res = await fetch(`${apiBase}/wordpress/categories?${q}`);
  let data = res.ok ? await parseJsonResponse(res) : null;
  if (Array.isArray(data) && data.length) return data;

  res = await fetch(`${wpBase}/categories?${q}`);
  if (!res.ok) return null;
  data = await parseJsonResponse(res);
  return Array.isArray(data) && data.length ? data : null;
}

async function fetchPostsByCategory(categoryId, maxPosts, apiBase, wpBase) {
  let res = await fetch(
    `${apiBase}/wordpress/posts?categories=${categoryId}&per_page=${maxPosts}`
  );
  let data = res.ok ? await parseJsonResponse(res) : null;
  if (Array.isArray(data) && data.length) return data;

  res = await fetch(
    `${wpBase}/posts?categories=${categoryId}&per_page=${maxPosts}&orderby=date&order=desc`
  );
  if (!res.ok) return [];
  data = await parseJsonResponse(res);
  return Array.isArray(data) ? data : [];
}

async function fetchLatestPosts(maxPosts, apiBase, wpBase) {
  let res = await fetch(`${apiBase}/wordpress/posts?per_page=${maxPosts}`);
  let data = res.ok ? await parseJsonResponse(res) : null;
  if (Array.isArray(data) && data.length) return data;

  res = await fetch(`${wpBase}/posts?per_page=${maxPosts}&orderby=date&order=desc`);
  if (!res.ok) return [];
  data = await parseJsonResponse(res);
  return Array.isArray(data) ? data : [];
}

const BlogSection = ({ categorySlug, title = "Latest Blogs", maxPosts = 3 }) => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (!categorySlug) return;

    const fetchPosts = async () => {
      const apiBase = getApiBase();
      const wpBase = getDirectWordPressRestBase();
      try {
        let categories = await fetchCategories(categorySlug, apiBase, wpBase);
        let data = [];
        if (categories?.length) {
          const categoryId = categories[0].id;
          data = await fetchPostsByCategory(categoryId, maxPosts, apiBase, wpBase);
        }
        if (!data.length) {
          data = await fetchLatestPosts(maxPosts, apiBase, wpBase);
        }
        setPosts(data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchPosts();
  }, [categorySlug, maxPosts]);

  if (!posts.length) return null;

  return (
    <section className="py-16 bg-[#F8FAFC]">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-semibold text-foreground">{title}</h2>
      </div>

      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 px-4 md:px-6">
        {posts.map(post => (
          <article key={post.id} className="bg-card border border-[#E2E8F0] rounded-3xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-md transition">
            <h3 className="text-xl font-semibold text-foreground mb-2" dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
            <p className="text-muted-foreground mb-4" dangerouslySetInnerHTML={{ __html: post.excerpt.rendered }} />
            <a
              href={wordPressPostPermalink(post)}
              className="text-blue-600 font-semibold hover:text-blue-700 hover:underline"
            >
              Read More
            </a>
          </article>
        ))}
      </div>
    </section>
  );
};

export default BlogSection;
